// Sign in, or — on a brand-new installation — create the workspace and its owner account.
import { useState } from 'react';
import { useLoginMutation, useSetupMutation, useSetupStatusQuery, errorMessage } from '../app/api';
import { useAppDispatch } from '../app/store';
import { setToken } from '../app/authSlice';
import { BrandLogo } from '../ui/BrandLogo';

function Input({ label, ...rest }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="field">
      <span>{label}</span>
      <input className="input" {...rest} />
    </label>
  );
}

function Setup({ currencies }: { currencies: string[] }) {
  const dispatch = useAppDispatch();
  const [setup, s] = useSetupMutation();
  const [form, setForm] = useState({ workspaceName: '', name: '', email: '', password: '', confirm: '', reportingCurrency: 'GBP' });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <form
      className="form-grid"
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        if (form.password !== form.confirm) return setError('Passwords do not match');
        try {
          const { confirm: _c, ...body } = form;
          void _c;
          dispatch(setToken((await setup(body).unwrap()).token));
        } catch (err) {
          setError(errorMessage(err));
        }
      }}
    >
      <Input label="Workspace name" placeholder="e.g. your brand" value={form.workspaceName} onChange={set('workspaceName')} required minLength={2} />
      <Input label="Your name" autoComplete="name" value={form.name} onChange={set('name')} required />
      <Input label="Email" type="email" autoComplete="username" value={form.email} onChange={set('email')} required />
      <Input label="Password" type="password" autoComplete="new-password" value={form.password} onChange={set('password')} required minLength={10} />
      <Input label="Confirm password" type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} required />
      <label className="field">
        <span>Reporting currency</span>
        <select className="select" value={form.reportingCurrency} onChange={set('reportingCurrency')}>
          {currencies.map((c) => (
            <option key={c} value={c}>
              {c}
              {c === 'GBP' ? ' (HMRC rates)' : ''}
            </option>
          ))}
        </select>
        <span className="hint">Totals across stores are converted at HMRC monthly rates.</span>
      </label>
      {error && (
        <div className="error-text" role="alert">
          {error}
        </div>
      )}
      <button className="btn primary" type="submit" disabled={s.isLoading} style={{ height: 38 }}>
        {s.isLoading ? 'Creating workspace…' : 'Create workspace'}
      </button>
    </form>
  );
}

function SignIn() {
  const dispatch = useAppDispatch();
  const [login, l] = useLoginMutation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="form-grid"
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        try {
          dispatch(setToken((await login({ email, password }).unwrap()).token));
        } catch (err) {
          setError(errorMessage(err));
        }
      }}
    >
      <Input label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <Input label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      {error && (
        <div className="error-text" role="alert">
          {error}
        </div>
      )}
      <button className="btn primary" type="submit" disabled={l.isLoading} style={{ height: 38 }}>
        {l.isLoading ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}

export function LoginPage() {
  const status = useSetupStatusQuery();
  const needsSetup = status.data?.needsSetup;
  return (
    <div className="login">
      <div className="login-card">
        <div style={{ display: 'grid', gap: 14 }}>
          {status.data?.branding?.logo ? (
            <BrandLogo logo={status.data.branding.logo} className="login-logo" />
          ) : (
            <div className="brand-mark" style={{ background: 'var(--accent-solid)' }} aria-hidden>
              {status.data?.branding?.productName?.slice(0, 2).toUpperCase() ?? ''}
            </div>
          )}
          <div>
            <h1>{needsSetup ? 'Set up your workspace' : 'Sign in'}</h1>
            <div className="muted">{needsSetup ? 'Create the owner account, then connect your Shopify stores.' : `${status.data?.branding?.productName ?? 'Channel analytics'} across all your stores`}</div>
          </div>
        </div>
        <div className="login-panel">
          {status.isLoading ? (
            <div className="skeleton" style={{ height: 160 }} aria-label="Loading" />
          ) : status.isError ? (
            <div className="error-text" role="alert">
              Cannot reach the server. {errorMessage(status.error)}
            </div>
          ) : needsSetup ? (
            <Setup currencies={status.data?.currencies ?? ['GBP']} />
          ) : (
            <SignIn />
          )}
        </div>
        {!needsSetup && <div className="hint" style={{ textAlign: 'center' }}>Accounts are created by your workspace administrator.</div>}
      </div>
    </div>
  );
}
